import Behaviour from "../../lib/ui/Behaviour";
import { Alt1GLOverlay } from "../../lib/alt1/alt1gl/overlay/Alt1GLOverlay";
import { observe } from "../../lib/reactive";
import lodash from "lodash";
import { mat4 } from "gl-matrix";
import { lazy } from "../../lib/Lazy";
import { Alt1GLProgram } from "../../lib/alt1/alt1gl/overlay/Alt1GLProgram";
import { Alt1 } from "../../lib/alt1/Alt1";
import { tilesize } from "../../lib/alt1/alt1gllib/ts/render/reflect3d";
import { GL_FLOAT, GL_UNSIGNED_BYTE, UniformSnapshotBuilder } from "../../lib/alt1/alt1gllib/ts/overlays";
import { Alt1GLCapturedFrame } from "../../lib/alt1/alt1gl/Alt1GLCapturedFrame";
export const WORLD_UNITS_PER_TILE = tilesize;
const SKIP_PROGRAM_MASK = 1 << 5;
export class SimpleGLOverlay extends Behaviour {
    constructor(geometry) {
        super();
        this.geometry = geometry;
        this.overlays = [];
    }
    updateOverlays(triggers) {
        // Partition existing overlays in keep/remove
        const [keep, remove] = lodash.partition(this.overlays, (o) => triggers.some(t => lodash.isEqual(t, o.renderFilter)));
        // Stop removed overlay
        remove.forEach(o => o.stop());
        this.overlays = keep;
        const new_triggers = triggers.filter(t => !this.overlays.some(o => lodash.isEqual(o.renderFilter, t)));
        new_triggers.forEach(t => {
            const uniforms = new UniformSnapshotBuilder({
                uModelMatrix: "mat4",
                uViewProjMatrix: "mat4"
            });
            const world_matrix = mat4.create();
            mat4.fromScaling(world_matrix, [WORLD_UNITS_PER_TILE, WORLD_UNITS_PER_TILE, WORLD_UNITS_PER_TILE]);
            uniforms.mappings.uModelMatrix.write(world_matrix);
            this.overlays.push(new Alt1GLOverlay(Alt1.instance().opengl(), t, SimpleGLOverlay.program.get(), this.geometry.vertexArray(), {
                uniformSources: [
                    { type: "program", name: "uViewProjMatrix", sourceName: "uViewProjMatrix" }
                ],
                uniformBuffer: uniforms.buffer
            }).start());
        });
        if (this.isActive())
            this.overlays.forEach(o => o.start());
    }
    begin() {
        // Subscribe to the observable triggers
        SimpleGLOverlay.TriggerManagement.instance.subscribe(t => this.updateOverlays(t), h => h.bindTo(this.lifetime_manager));
    }
    end() {
        this.overlays.forEach(o => o.stop());
    }
}
(function (SimpleGLOverlay) {
    SimpleGLOverlay.program = lazy(() => {
        const vertshader = `
    #version 330 core
    layout (location = 0) in vec3 aPos;
    layout (location = 6) in vec4 aColor;
    uniform highp mat4 uModelMatrix;
    uniform highp mat4 uViewProjMatrix;
    uniform highp vec2 uMouse;
    out vec4 ourColor;
    out vec3 FragPos;
    void main() {
        vec4 worldpos = uModelMatrix * vec4(aPos, 1.);
        gl_Position = uViewProjMatrix * worldpos;
        FragPos = worldpos.xyz/worldpos.w;
        ourColor = aColor;
    }`;
        const fragshader = `
    #version 330 core
    in vec4 ourColor;
    out vec4 FragColor;
    void main() {
        FragColor = ourColor;
    }`;
        return new Alt1GLProgram(vertshader, fragshader, [
            { location: 0, name: "aPos", type: GL_FLOAT, length: 3 },
            { location: 6, name: "aColor", type: GL_UNSIGNED_BYTE, length: 4 }
        ], new UniformSnapshotBuilder({
            uModelMatrix: "mat4",
            uViewProjMatrix: "mat4"
        }).args);
    });
    class TriggerManagement extends Behaviour {
        constructor() {
            super();
            this.active_triggers = observe([]).structuralEquality();
            this.stream = null;
        }
        end() {
            this.stream?.endLifetime();
            this.stream = null;
        }
        begin() {
        }
        updateStreamState() {
            if (!Alt1.instance().featureGL())
                return; // Silently fail if alt1gl is not available
            const should_exist = this.active_triggers.observerCount() != 0;
            const exists = !!this.stream;
            if (should_exist && !exists) {
                this.stream = Alt1GLCapturedFrame.subscribe({
                    features: ["uniforms"],
                    framecooldown: 10000,
                    skipProgramMask: SKIP_PROGRAM_MASK,
                }, frames => {
                    const new_triggers = [...this.active_triggers.value()];
                    for (const render of frames.renders) {
                        if (TriggerManagement.isProgramRelevantForChunkRendering(render.raw.program)) {
                            if (!new_triggers.some(p => p.programId == render.raw.program.programId)) {
                                new_triggers.push({ programId: render.raw.program.programId, /*maxPerFrame: 1*/ });
                            }
                        }
                        else {
                            // Mark irrelevant programs with our skip mask so we do not check them again
                            render.raw.program.skipmask |= SKIP_PROGRAM_MASK;
                        }
                    }
                    this.active_triggers.set(new_triggers);
                    console.log(`New trigger count ${new_triggers.length}`);
                });
            }
            else if (!should_exist && exists) {
                this.stream.endLifetime();
                this.stream = null;
            }
        }
        subscribe(f, handler_f = null) {
            this.active_triggers.subscribe(f, true, handler_f);
            this.updateStreamState();
        }
        static get instance() {
            return TriggerManagement._instance.get();
        }
    }
    TriggerManagement._instance = lazy(() => new TriggerManagement().start());
    SimpleGLOverlay.TriggerManagement = TriggerManagement;
    (function (TriggerManagement) {
        function isProgramRelevantForChunkRendering(program) {
            return program.inputs.some(q => q.name == "aMaterialSettingsSlotXY3");
        }
        TriggerManagement.isProgramRelevantForChunkRendering = isProgramRelevantForChunkRendering;
    })(TriggerManagement = SimpleGLOverlay.TriggerManagement || (SimpleGLOverlay.TriggerManagement = {}));
})(SimpleGLOverlay || (SimpleGLOverlay = {}));
